/** Addresses of pictures and video uploaded into an ad, on our own route.
 *
 * The upload endpoint hands back /api/v2/jobs/media/<token>/<filename> —
 * absolute on the jobs domain, root-relative on a dev checkout that has none
 * (core/logic/job_ad_media.py). Both the image and the video rule need to
 * recognise one, so the shape lives here rather than in either.
 */

const MEDIA_PATH = "/api/v2/jobs/media/";

/** Whether this source is an ad's own uploaded file rather than a foreign URL. */
export function isUploadedMediaSrc(src: string): boolean {
	if (src.startsWith(MEDIA_PATH)) return true;
	return /^https:\/\/[^/]+\/api\/v2\/jobs\/media\/[0-9a-f]{32}\//i.test(src);
}

/** The extensions the upload endpoint accepts for video (see VIDEO_EXTENSIONS). */
export const UPLOADED_VIDEO_EXTENSIONS = [".mp4", ".webm"] as const;

/** The extensions it accepts for pictures — IMAGE_EXTENSIONS plus the HEIC it
 *  converts (core/logic/job_ad_media.py). .heic is here and not in
 *  image-embed.ts on purpose: an ad may never carry one, but a recruiter may
 *  upload one, because the server turns it into a JPEG on the way in. */
export const UPLOADED_IMAGE_EXTENSIONS = [
	".png",
	".jpg",
	".jpeg",
	".webp",
	".gif",
	".avif",
	".heic",
	".heif",
] as const;

/** Whether a dropped file is something the upload endpoint would take.
 *
 * By extension as well as MIME type, because a browser reports what the
 * operating system told it and Windows hands Chrome an EMPTY type for .HEIC
 * unless the HEIF extension is installed — which made the editor refuse a
 * photo straight off an iPhone that the server would have accepted and
 * converted. What may go in an ad is the server's ruling, not the OS's. */
export function isUploadableMedia(file: File): boolean {
	if (file.type.startsWith("image/") || file.type.startsWith("video/")) return true;
	const name = file.name.toLowerCase();
	return [...UPLOADED_IMAGE_EXTENSIONS, ...UPLOADED_VIDEO_EXTENSIONS].some(
		(extension) => name.endsWith(extension)
	);
}

/** Video rather than picture, by the same two signals. */
export function isUploadableVideo(file: File): boolean {
	if (file.type.startsWith("video/")) return true;
	const name = file.name.toLowerCase();
	return UPLOADED_VIDEO_EXTENSIONS.some((extension) => name.endsWith(extension));
}

/** An uploaded file that is a video, by its address alone. */
export function isUploadedVideoSrc(src: string): boolean {
	if (!isUploadedMediaSrc(src)) return false;
	const path = src.split("?")[0].toLowerCase();
	return UPLOADED_VIDEO_EXTENSIONS.some((extension) => path.endsWith(extension));
}
